import { KeyboardEventHandler, MouseEventHandler, PropsWithChildren, useMemo, useRef } from "react";
import { useDataId } from "utils/hooks";

import { ImageDownload } from "components/utility/images";
import PDFConvertor from "components/utility/pdf/PDFConvertor";
import DotLoader from "components/utility/dot-loader/DotLoader";
import { DefaultComponent } from "types/DefaultComponent";

export interface ButtonIconDownloadProps extends DefaultComponent {
	color?: string;
	downloadUrl?: string;
	dataA?: string;
	dataAEvt?: string;
	dataAComp?: string;
	dataAName?: string;
	onClick?: MouseEventHandler<HTMLButtonElement>;
}

function ButtonIconDownload({
	color = "default",
	downloadUrl,
	dataQA = "button-icon-download",
	dataQAPrefix = "",
	dataQASuffix = "",
	dataA = "",
	dataAEvt = "",
	dataAComp = "",
	dataAName = "",
	onClick
}: PropsWithChildren<ButtonIconDownloadProps>) {
	const dataQAId = useDataId(dataQA, { prefix: dataQAPrefix, suffix: dataQASuffix });
	const bodyMaskRef = useRef<HTMLDivElement>(null);
	const downloadRef = useRef<HTMLDivElement>(null);
	const dotLoaderRef = useRef<HTMLDivElement>(null);

	const className = useMemo(() => {
		return `button-icon button-icon-${color} button-download`;
	}, [color]);

	/**
	 * Overlay. Use's defined ref's to mask and hide buttons
	 */
	const maskBody = () => {
		if (bodyMaskRef.current && downloadRef.current && dotLoaderRef.current) {
			bodyMaskRef.current.classList.add("body-mask-show");
			downloadRef.current.classList.add("button-icon-hide");
			dotLoaderRef.current.classList.remove("button-icon-hide");
		}

		setTimeout(() => {
			//-- PDFConverter doesn't require a constructor argument any longer
			const pdfConvertor = new PDFConvertor();
			pdfConvertor.convert();
		}, 500);
	};

	/**
	 * handleKeyPress
	 * @param e KeyboardEvent : Synthetic handler for keyboard event
	 * @see https://www.w3.org/TR/uievents-key/#keys-whitespace
	 */
	const handleKeyPress: KeyboardEventHandler<HTMLButtonElement> = (e) => {
		switch (e.key) {
			case "Enter":
			case "Space":
				maskBody();
				break;
		}
	};

	if (downloadUrl && downloadUrl.length > 0) {
		return (
			<a
				download={downloadUrl}
				aria-label={"download"}
				href={downloadUrl}
				className={className}
				data-qa={dataQAId}
				data-a={dataA}
				data-e-evt={dataAEvt}
				data-a-comp={dataAComp}
				data-a-name={dataAName}
			>
				<ImageDownload />
			</a>
		);
	} else {
		return (
			<>
				<button
					type="button"
					aria-label={"download"}
					className={className}
					data-qa={dataQAId}
					data-a={dataA}
					data-a-evt={dataAEvt}
					data-a-comp={dataAComp}
					data-a-name={dataAName}
					onClick={() => maskBody()}
					onKeyUp={(e) => handleKeyPress(e)}
					onMouseUp={onClick}
				>
					<div className="button-icon-display download-button-placeholder" ref={downloadRef}>
						<ImageDownload />
					</div>
					<div
						className="button-icon-display dot-loader-placeholder button-icon-hide"
						ref={dotLoaderRef}
					>
						<DotLoader color="white" />
					</div>
				</button>
				<div
					className="body-mask body-mask-indicator pdf--ignore"
					tabIndex={0}
					role="button"
					ref={bodyMaskRef}
				>
					<div className="dot-loader-background-white">
						<div className="dot-loader-middle">
							<DotLoader className="dot-loader" />
							<p className="dot-loader-content">Generating PDF...</p>
						</div>
					</div>
				</div>
			</>
		);
	}
}

export default ButtonIconDownload;
